/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import ActionDrawerStore from 'stores/ActionDrawerStore';
import UserStore from 'stores/UserStore';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';

import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import FormSection from 'components/FormSection/FormSection';
import FormControls from 'forms/components/controls/FormControls/FormControls';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';

import {
  autoCompleteManufacturersForAccount,
  autoCompleteProductsForManufacturer,
  autoCompleteTradingAccountsForRequestLicenseForm,
} from 'api/forms.js';
import { autoCompleteLicenseTypes } from './utilities/autocompletes';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { createLicenseRequestForm, makeFields } from './setupLicenseRequestForm';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ElectroRadioGroup from '../components/inputs/ElectroRadioGroup/ElectroRadioGroup';

type InjectedProps = {
  actionDrawerStore: ActionDrawerStore,
  userStore: UserStore,
} & TranslatorProps;

export type LicenseType = $Values<typeof LicenseTypes>;
export const LicenseTypes = Object.freeze({
  REQUEST_NEW_LICENSE: 'Request New License',
  TRANSFER_EXISTING_LICENSE: 'Transfer Existing License',
  REMOVE_CURRENT_LICENSE: 'Remove Current License',
});

export type PONumberValues = 'use_existing' | 'use_new';
export type LicenseRequestLocalState = {
  responseMessage: string,
  showResponseMessage: boolean,
  isLoading: boolean,
  hasSuccess: boolean,
  hasError: boolean,
  licenseType: LicenseType,
  poNumberRadioValue: PONumberValues,
};

@translate()
@inject('actionDrawerStore', 'userStore')
@observer
export default class LicenseRequestForm extends ComponentWithInjectedProps<{}, {}, InjectedProps> {
  form = this.makeForm();

  @observable accountsModel = new AutoCompleteModel(
    autoCompleteTradingAccountsForRequestLicenseForm
  );

  @observable licenseTypeModel = new AutoCompleteModel(autoCompleteLicenseTypes);

  @observable manufacturerModel = new AutoCompleteModel(autoCompleteManufacturersForAccount);

  @observable productModel = new AutoCompleteModel(autoCompleteProductsForManufacturer);

  @observable localState: LicenseRequestLocalState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    hasSuccess: false,
    hasError: false,
    licenseType: LicenseTypes.REQUEST_NEW_LICENSE,
    poNumberRadioValue: 'use_existing',
  };

  componentDidMount() {
    this.setFieldDefaults();
  }

  makeForm() {
    const fieldOptions = makeFields(this.props.t, this.localState);

    return createLicenseRequestForm(fieldOptions, this.localState, this.props.t);
  }

  updateFormRules() {
    const { rules } = makeFields(this.props.t, this.localState);
    this.form.set('rules', rules);
  }

  renderTradingAccountSection(form: Object) {
    const { accountsModel, manufacturerModel } = this;

    return (
      <FormSection form={form} section="chooseAccount">
        <ElectroSelectExtra
          field={form.$('chooseAccount.tradingAccount')}
          form={form}
          formName="chooseAccount"
          labelKey="name"
          props={{
            model: accountsModel,
            modelChild: [manufacturerModel],
          }}
        />
      </FormSection>
    );
  }

  renderChooseLicenseSection(form: Object) {
    const { licenseTypeModel } = this;

    return (
      <FormSection form={form} section="chooseLicense">
        <ElectroSelectExtra
          field={form.$('chooseLicense.type')}
          form={form}
          formName="chooseAccount"
          labelKey="name"
          handleOnChange={this.onLicenseTypeChange.bind(this)}
          props={{
            model: licenseTypeModel,
          }}
        />
      </FormSection>
    );
  }

  onLicenseTypeChange(value: Object) {
    this.localState.licenseType = value.name;

    this.updateFormRules();
  }

  renderLicenseDetailsSection(form: Object) {
    const { manufacturerModel, productModel } = this;

    return (
      <FormSection form={form} section="licenseDetails">
        <ElectroSelectExtra
          field={form.$('licenseDetails.manufacturer')}
          form={form}
          formName="licenseDetails"
          labelKey="name"
          props={{
            model: manufacturerModel,
            modelChild: productModel,
          }}
        />
        <ElectroSelectExtra
          field={form.$('licenseDetails.product')}
          form={form}
          formName="licenseDetails"
          labelSeparator=" - "
          labelKey={['model', 'product_group_description']}
          props={{
            model: productModel,
          }}
        />
      </FormSection>
    );
  }

  renderRequiredDateSection(form: Object) {
    return (
      <FormSection form={form} section="requiredDate">
        <ElectroDatePicker field={form.$('requiredDate.date')} />
      </FormSection>
    );
  }

  renderAssetDetailsSection(form: Object) {
    const { licenseType }: LicenseType = this.localState;

    switch (licenseType) {
      case 'Request New License':
        return (
          <FormSection form={form} section={'newAssetDetails'}>
            <ElectroTextField field={form.$('newAssetDetails.assetNumber')} />
            <ElectroTextField field={form.$('newAssetDetails.serialNumber')} />
          </FormSection>
        );
      case 'Transfer Existing License':
        return (
          <React.Fragment>
            <FormSection form={form} section={'newAssetDetails'}>
              <ElectroTextField field={form.$('newAssetDetails.assetNumber')} />
              <ElectroTextField field={form.$('newAssetDetails.serialNumber')} />
            </FormSection>
            <FormSection form={form} section={'assetDetails'}>
              <ElectroTextField field={form.$('assetDetails.assetNumber')} />
              <ElectroTextField field={form.$('assetDetails.serialNumber')} />
            </FormSection>
          </React.Fragment>
        );
      case 'Remove Current License':
        return (
          <FormSection form={form} section={'assetDetails'}>
            <ElectroTextField field={form.$('assetDetails.assetNumber')} />
            <ElectroTextField field={form.$('assetDetails.serialNumber')} />
            <ElectroTextField field={form.$('assetDetails.returnKeyCode')} />
          </FormSection>
        );
    }
  }

  renderCustomerDetailsSection(form: Object) {
    return (
      <FormSection form={form} section={'customerDetails'}>
        <ElectroTextField disabled={true} field={form.$('customerDetails.name')} />
        <ElectroTextField disabled={true} field={form.$('customerDetails.requestDate')} />
      </FormSection>
    );
  }

  renderPurchaseOrderNumberSection(form: Object) {
    const { poNumberRadioValue } = this.localState;

    return (
      <FormSection form={form} section={'poNumber'}>
        <ElectroRadioGroup
          field={this.form.$('poNumber.chooseExistingOrNew')}
          inline={true}
          inputLabel={true}
          onChange={this.onPOOptionChange.bind(this)}
        />
        {poNumberRadioValue === 'use_new' && <ElectroTextField field={form.$('poNumber.number')} />}
      </FormSection>
    );
  }

  onPOOptionChange(value) {
    this.localState.poNumberRadioValue = value;

    this.updateFormRules();
  }

  setFieldDefaults() {
    const {
      userStore: { activeUser },
    } = this.props;
    const today = new Date().toDateString();

    this.form.$('customerDetails.id').set('value', activeUser.id);
    this.form.$('customerDetails.name').set('value', activeUser.full_name);
    this.form.$('customerDetails.requestDate').set('value', today);
    this.form.$('poNumber.chooseExistingOrNew').set('value', this.localState.poNumberRadioValue);
  }

  renderCommentsSection(form: Object) {
    return (
      <FormSection form={form} section={'comments'}>
        <ElectroTextField
          field={form.$('comments.comment')}
          props={{
            multiline: true,
            rows: 5,
          }}
        />
      </FormSection>
    );
  }

  renderFormControlsSection(form: Object) {
    return <FormControls buttonText={'Submit License Request'} form={form} />;
  }

  render() {
    const { form } = this;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <ElectroResponseMessage localState={this.localState} />
          <FormWrapper form={form} title={'New License Request'}>
            {this.renderTradingAccountSection(form)}
            {this.renderChooseLicenseSection(form)}
            {this.renderLicenseDetailsSection(form)}
            {this.renderRequiredDateSection(form)}
            {this.renderAssetDetailsSection(form)}
            {this.renderCustomerDetailsSection(form)}
            {this.renderPurchaseOrderNumberSection(form)}
            {this.renderCommentsSection(form)}
            {this.renderFormControlsSection(form)}
          </FormWrapper>
        </PageWrapper>
      </React.Fragment>
    );
  }
}
